/**
 * Forms-engine-driven patient registration page (PRD.151).
 *
 * Renders a published form definition tagged as a registration form,
 * collects field values in local state, and on submit:
 *   1. Creates the patient row
 *   2. Upserts the patient_contacts row
 *   3. Routes two_way bound fields through the save orchestrator
 *   4. Stores custom/unbound/default_only values as form_instance_values
 *
 * The same schema is reusable later for grouped edit screens.
 */
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavIcon, useBrandTokens } from '../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../adapters/powersync';
import {
  qPublishedFormDefinitions,
  qActiveFormByPurpose,
  qFormTemplatePages,
  qFormTemplateMappings,
  parseFieldsSchemaV1,
  flattenFields,
  type FormDefinitionRow,
  type FormTemplatePageRow,
  type FormTemplateMappingRow,
  type FormFieldsSchemaV1,
  orchestrateTwoWaySave,
  buildInsertSql,
  generateInitialValues,
  getGeneratedFields,
} from '@helios/data-access';
import { useAuth } from '@helios/auth-firebase-web';
import {
  SectionRenderer,
  TemplateOverlayView,
  ModeSwitch,
} from './patient/encounters/FormFieldRenderers';

// ─── Helpers ───

function genId(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// ─── Structured Validation ───

type SubmitIssueSeverity = 'blocking' | 'warning';
type SubmitIssueLayer = 'form' | 'identity' | 'persistence';

interface SubmitIssue {
  severity: SubmitIssueSeverity;
  layer: SubmitIssueLayer;
  field?: string;
  message: string;
}

/**
 * Validate form values before persistence. Returns blocking issues that
 * must prevent submission and warnings that should be surfaced.
 */
function validateFormFields(
  schema: FormFieldsSchemaV1,
  values: Record<string, string>,
): SubmitIssue[] {
  const issues: SubmitIssue[] = [];
  const allFields = flattenFields(schema);

  // Required field validation
  for (const field of allFields) {
    if (field.required && !values[field.key]?.trim()) {
      issues.push({
        severity: 'blocking',
        layer: 'form',
        field: field.key,
        message: `${field.label || field.key} is required`,
      });
    }
  }

  // Patient name is always required for registration
  const nameField = allFields.find(
    (f) => f.binding?.source === 'patient' && f.binding?.bindingKey === 'full_name',
  );
  if (nameField && !values[nameField.key]?.trim()) {
    // Only add if not already flagged by required-field check
    if (!issues.some((i) => i.field === nameField.key)) {
      issues.push({
        severity: 'blocking',
        layer: 'form',
        field: nameField.key,
        message: 'Patient name is required for registration',
      });
    }
  }

  return issues;
}

/**
 * Check for duplicate patient number. Returns a blocking issue if found.
 */
async function validatePatientIdentity(
  db: { getAll: <T>(sql: string, params?: unknown[]) => Promise<T[]> },
  schema: FormFieldsSchemaV1,
  values: Record<string, string>,
): Promise<SubmitIssue[]> {
  const issues: SubmitIssue[] = [];
  const allFields = flattenFields(schema);

  // Find the patient_number field
  const pnField = allFields.find(
    (f) => f.binding?.source === 'patient' && f.binding?.bindingKey === 'patient_number',
  );
  const patientNumber = pnField ? values[pnField.key]?.trim() : undefined;

  if (patientNumber) {
    const dupes = await db.getAll<{ id: string }>(
      `SELECT id FROM patients WHERE patient_number = ? LIMIT 1`,
      [patientNumber],
    );
    if (dupes.length > 0) {
      issues.push({
        severity: 'blocking',
        layer: 'identity',
        field: pnField!.key,
        message: `A patient with number "${patientNumber}" already exists`,
      });
    }
  }

  return issues;
}

// ─── Registration Form Page ───

export function RegistrationFormPage() {
  const navigate = useNavigate();
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  // ── Auto-resolve designated registration form ──
  const { data: designatedRows = [] } = usePowerSyncQuery<FormDefinitionRow>(
    qActiveFormByPurpose(orgId, 'registration'),
  );
  const designatedForm = designatedRows[0] ?? null;

  // ── Fallback: published forms for manual selection ──
  const { data: publishedDefs = [] } = usePowerSyncQuery<FormDefinitionRow>(
    qPublishedFormDefinitions(orgId),
  );

  // ── Form definition resolution ──
  // Auto-resolve from designated form; fall back to manual selection if none designated
  const [manualDefId, setManualDefId] = useState<string | null>(null);
  const definition =
    designatedForm ??
    (manualDefId ? (publishedDefs.find((d) => d.id === manualDefId) ?? null) : null);
  const isManualMode = !designatedForm;

  const schema = useMemo<FormFieldsSchemaV1>(() => {
    if (!definition) return { version: 1, sections: [] };
    return parseFieldsSchemaV1(definition.fields_schema);
  }, [definition?.id, definition?.fields_schema]);

  const allFields = useMemo(() => flattenFields(schema), [schema]);

  // ── Template data (for render mode) ──
  const { data: templatePages = [] } = usePowerSyncQuery<FormTemplatePageRow>(
    qFormTemplatePages(definition?.id ?? ''),
  );
  const { data: templateMappings = [] } = usePowerSyncQuery<FormTemplateMappingRow>(
    qFormTemplateMappings(definition?.id ?? ''),
  );
  const defaultView =
    definition?.default_view === 'render' && templatePages.length > 0 ? 'render' : 'form';
  const [viewMode, setViewMode] = useState<'form' | 'render'>('form');
  // Reset view mode when definition changes
  useMemo(() => setViewMode(defaultView), [defaultView]);

  // ── Local form state ──
  const [values, setValues] = useState<Record<string, string>>({});
  const [signatures, setSignatures] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [issues, setIssues] = useState<SubmitIssue[]>([]);
  const [createdPatientId, setCreatedPatientId] = useState<string | null>(null);
  const [createdPatientName, setCreatedPatientName] = useState('');

  const handleValueChange = useCallback((fieldKey: string, val: string) => {
    setValues((prev) => ({ ...prev, [fieldKey]: val }));
    setIssues((prev) => prev.filter((i) => i.field !== fieldKey));
  }, []);

  const handleSignatureSave = useCallback(
    (_fieldKey: string, _dataUrl: string, strokeData: string) => {
      setSignatures((prev) => ({ ...prev, [_fieldKey]: strokeData }));
    },
    [],
  );

  // No binding data for new patients — there's no existing patient to resolve from
  const bindingData: Record<string, string> = useMemo(() => {
    const map: Record<string, string> = {};
    map['form_context.current_date'] = new Date().toISOString().slice(0, 10);
    return map;
  }, []);

  // ── Auto-generate field values (e.g. patient number) ──
  const generatedFields = useMemo(() => getGeneratedFields(schema), [schema]);
  const generationRanRef = useRef(false);

  useEffect(() => {
    if (!db || generatedFields.length === 0 || generationRanRef.current) return;
    generationRanRef.current = true;
    (async () => {
      const generated = await generateInitialValues(db, schema, values);
      if (Object.keys(generated).length > 0) {
        setValues((prev) => ({ ...prev, ...generated }));
      }
    })();
  }, [db, generatedFields.length, schema]);

  // Reset generation flag when definition changes (e.g. user picks different form)
  useEffect(() => {
    generationRanRef.current = false;
  }, [definition?.id]);

  // Derived issue helpers
  const blockingIssues = useMemo(() => issues.filter((i) => i.severity === 'blocking'), [issues]);

  // ── Submit handler ──
  const handleSubmit = useCallback(async () => {
    if (!db || !definition || isSaving || createdPatientId) return;
    setIsSaving(true);
    setIssues([]);

    const collectedIssues: SubmitIssue[] = [];

    try {
      // ── Layer 1: Form-level validation ──
      const formIssues = validateFormFields(schema, values);
      collectedIssues.push(...formIssues);

      if (formIssues.some((i) => i.severity === 'blocking')) {
        setIssues(collectedIssues);
        setIsSaving(false);
        return;
      }

      // ── Layer 2: Identity validation ──
      const identityIssues = await validatePatientIdentity(db, schema, values);
      collectedIssues.push(...identityIssues);

      if (identityIssues.some((i) => i.severity === 'blocking')) {
        setIssues(collectedIssues);
        setIsSaving(false);
        return;
      }

      // ── Layer 3: Persistence ──
      const patientId = genId();
      const orchestratorResult = orchestrateTwoWaySave(schema, values);

      // 3a. Create patient row — this is the anchor; if it fails, stop everything
      const patientOp = orchestratorResult.operations.find((op) => op.table === 'patients');
      const patientColumns: Record<string, string> = {
        id: patientId,
        status: 'active',
        ...(patientOp?.columns ?? {}),
      };
      const patientKeys = Object.keys(patientColumns);
      const patientPlaceholders = patientKeys.map(() => '?').join(', ');
      const patientVals = patientKeys.map((k) => patientColumns[k]);

      try {
        await db.execute(
          `INSERT INTO patients (${patientKeys.join(', ')}, created_at, updated_at) VALUES (${patientPlaceholders}, datetime('now'), datetime('now'))`,
          patientVals,
        );
      } catch (err) {
        collectedIssues.push({
          severity: 'blocking',
          layer: 'persistence',
          message: `Failed to create patient: ${err instanceof Error ? err.message : 'unknown error'}`,
        });
        setIssues(collectedIssues);
        setIsSaving(false);
        return; // Hard stop — dependent writes must not proceed
      }

      // 3b. Insert patient_contacts (dependent on patient existing)
      const contactOp = orchestratorResult.operations.find((op) => op.table === 'patient_contacts');
      if (contactOp && Object.keys(contactOp.columns).length > 0) {
        try {
          const { sql, params } = buildInsertSql(contactOp, {
            id: genId(),
            patient_id: patientId,
          });
          await db.execute(sql, params);
        } catch (err) {
          collectedIssues.push({
            severity: 'warning',
            layer: 'persistence',
            message: `Contact info saved partially: ${err instanceof Error ? err.message : 'unknown error'}`,
          });
        }
      }

      // 3c. Multi-row operations (e.g. patient_allergies)
      for (const mop of orchestratorResult.multiRowOps) {
        try {
          // Soft-delete existing rows for this type
          await db.execute(
            `UPDATE ${mop.table} SET deleted_at = datetime('now') WHERE patient_id = ? AND ${mop.filterColumn} = ? AND deleted_at IS NULL`,
            [patientId, mop.filterValue],
          );
          // Insert new rows
          for (const val of mop.values) {
            const staticEntries = Object.entries(mop.staticColumns);
            const cols = [
              'id',
              'patient_id',
              mop.valueColumn,
              ...staticEntries.map(([k]) => k),
              'updated_at',
            ];
            const placeholders = cols.map((c) => (c === 'updated_at' ? "datetime('now')" : '?'));
            const params = [genId(), patientId, val, ...staticEntries.map(([, v]) => v)];
            await db.execute(
              `INSERT INTO ${mop.table} (${cols.join(', ')}) VALUES (${placeholders.join(', ')})`,
              params,
            );
          }
        } catch (err) {
          collectedIssues.push({
            severity: 'warning',
            layer: 'persistence',
            message: `${mop.description} partially failed: ${err instanceof Error ? err.message : 'unknown error'}`,
          });
        }
      }

      // 3d. Create form_instance + form_instance_values
      const instanceId = genId();
      try {
        await db.execute(
          `INSERT INTO form_instances (id, org_id, form_definition_id, patient_id, status, created_at, updated_at, completed_at) VALUES (?, ?, ?, ?, 'completed', datetime('now'), datetime('now'), datetime('now'))`,
          [instanceId, orgId, definition.id, patientId],
        );

        for (const [fieldKey, val] of Object.entries(values)) {
          if (!val) continue;
          await db.execute(
            `INSERT INTO form_instance_values (id, form_instance_id, field_key, value, created_at, updated_at) VALUES (?, ?, ?, ?, datetime('now'), datetime('now'))`,
            [genId(), instanceId, fieldKey, val],
          );
        }
      } catch (err) {
        collectedIssues.push({
          severity: 'warning',
          layer: 'persistence',
          message: `Form response record saved partially: ${err instanceof Error ? err.message : 'unknown error'}`,
        });
      }

      // ── Done — patient was created even if secondary writes had warnings ──
      const allFields = flattenFields(schema);
      const nameField = allFields.find(
        (f) => f.binding?.bindingKey === 'full_name' && f.binding?.source === 'patient',
      );
      setIssues(collectedIssues);
      setCreatedPatientId(patientId);
      setCreatedPatientName(nameField ? (values[nameField.key] ?? '') : '');
    } catch (err) {
      // Unexpected error outside the per-step handlers
      collectedIssues.push({
        severity: 'blocking',
        layer: 'persistence',
        message: err instanceof Error ? err.message : 'Registration failed unexpectedly',
      });
      setIssues(collectedIssues);
    } finally {
      setIsSaving(false);
    }
  }, [db, definition, schema, values, isSaving, createdPatientId, orgId]);

  // ── Success state ──
  if (createdPatientId) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--sp-4)',
          padding: 'var(--sp-8)',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'var(--color-success-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <NavIcon name="check-circle" />
        </div>
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-xl)', color: 'var(--text)' }}>
          Patient Created
        </h2>
        {createdPatientName && (
          <p style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text-muted)' }}>
            {createdPatientName}
          </p>
        )}
        {issues.filter((i) => i.severity === 'warning').length > 0 && (
          <div
            style={{
              padding: 'var(--sp-3) var(--sp-4)',
              background: 'var(--color-warning-bg, #fefce8)',
              border: '1px solid var(--color-warning-text, #a16207)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-warning-text, #a16207)',
              fontSize: 'var(--font-size-sm)',
              maxWidth: 400,
            }}
          >
            <ul style={{ margin: 0, paddingLeft: 'var(--sp-4)' }}>
              {issues
                .filter((i) => i.severity === 'warning')
                .map((issue, i) => (
                  <li key={i}>{issue.message}</li>
                ))}
            </ul>
          </div>
        )}
        <div style={{ display: 'flex', gap: 'var(--sp-3)', marginTop: 'var(--sp-2)' }}>
          <button
            onClick={() => navigate(`/patient/${createdPatientId}`)}
            style={{
              height: 40,
              padding: '0 20px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: primary,
              color: '#fff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
            }}
          >
            Go to Patient
          </button>
          <button
            onClick={() => {
              setCreatedPatientId(null);
              setCreatedPatientName('');
              setValues({});
              setIssues([]);
            }}
            style={{
              height: 40,
              padding: '0 20px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
            }}
          >
            Register Another
          </button>
        </div>
      </div>
    );
  }

  // ── No form resolved — show fallback ──
  if (!definition) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-3)',
          }}
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted)',
              padding: 4,
            }}
          >
            <NavIcon name="arrow-left" />
          </button>
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
            }}
          >
            New Patient Registration
          </h1>
        </div>
        <div style={{ padding: 'var(--sp-5)', maxWidth: 600 }}>
          {/* No designated registration form — show info + manual fallback */}
          <div
            style={{
              padding: 'var(--sp-3) var(--sp-4)',
              marginBottom: 'var(--sp-4)',
              background: 'var(--color-info-bg, #eff6ff)',
              border: '1px solid var(--color-info, #3b82f6)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-info, #1d4ed8)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            No registration form has been designated. To set one, edit a published form in Settings
            &rarr; Forms and assign it the &ldquo;Registration&rdquo; purpose.
          </div>
          {publishedDefs.length > 0 && (
            <>
              <p
                style={{
                  margin: '0 0 var(--sp-3)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                }}
              >
                Or select a published form manually:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                {publishedDefs.map((def) => (
                  <button
                    key={def.id}
                    onClick={() => setManualDefId(def.id)}
                    style={{
                      padding: 'var(--sp-3) var(--sp-4)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-surface)',
                      color: 'var(--text)',
                      fontSize: 'var(--font-size-sm)',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <span style={{ fontWeight: 'var(--font-weight-medium)' }}>{def.name}</span>
                    {def.description && (
                      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                        {def.description}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Form rendering ──
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div
        style={{
          padding: 'var(--sp-3) var(--sp-5)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <button
            onClick={() => (isManualMode ? setManualDefId(null) : navigate(-1))}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted)',
              padding: 4,
            }}
          >
            <NavIcon name="arrow-left" />
          </button>
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
            }}
          >
            {definition.name}
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          {templatePages.length > 0 && (
            <ModeSwitch mode={viewMode} onChange={setViewMode} primary={primary} />
          )}
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            style={{
              height: 36,
              padding: '0 20px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: isSaving ? 'var(--text-muted)' : primary,
              color: '#fff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: isSaving ? 'default' : 'pointer',
              opacity: isSaving ? 0.7 : 1,
            }}
          >
            {isSaving ? 'Saving...' : 'Register Patient'}
          </button>
        </div>
      </div>

      {/* Issues banner */}
      {issues.length > 0 && (
        <div
          style={{
            margin: 'var(--sp-4) var(--sp-5) 0',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2)',
          }}
        >
          {blockingIssues.length > 0 && (
            <div
              style={{
                padding: 'var(--sp-3) var(--sp-4)',
                background: 'var(--color-danger-bg, #fef2f2)',
                border: '1px solid var(--color-danger-border, #fecaca)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-danger-text, #dc2626)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <ul style={{ margin: 0, paddingLeft: 'var(--sp-4)' }}>
                {blockingIssues.map((issue, i) => (
                  <li key={i}>{issue.message}</li>
                ))}
              </ul>
            </div>
          )}
          {issues.filter((i) => i.severity === 'warning').length > 0 && (
            <div
              style={{
                padding: 'var(--sp-3) var(--sp-4)',
                background: 'var(--color-warning-bg, #fefce8)',
                border: '1px solid var(--color-warning-text, #a16207)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-warning-text, #a16207)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <ul style={{ margin: 0, paddingLeft: 'var(--sp-4)' }}>
                {issues
                  .filter((i) => i.severity === 'warning')
                  .map((issue, i) => (
                    <li key={i}>{issue.message}</li>
                  ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Content — Form mode or Render mode */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {viewMode === 'form' ? (
          <div style={{ padding: 'var(--sp-5)' }}>
            <div
              style={{
                maxWidth: 720,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-5)',
              }}
            >
              {schema.sections.map((section) => (
                <SectionRenderer
                  key={section.id}
                  section={section}
                  values={values}
                  signatures={signatures}
                  onValueChange={handleValueChange}
                  onSignatureSave={handleSignatureSave}
                  readOnly={false}
                  bindingData={bindingData}
                />
              ))}
              {schema.sections.length === 0 && (
                <div
                  style={{
                    padding: 'var(--sp-8)',
                    textAlign: 'center',
                    color: 'var(--text-muted)',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  This form has no sections or fields defined.
                </div>
              )}
            </div>
          </div>
        ) : (
          <TemplateOverlayView
            pages={templatePages}
            mappings={templateMappings}
            fields={allFields}
            values={values}
            signatures={signatures}
            schema={schema}
            bindingData={bindingData}
            onValueChange={handleValueChange}
            onSignatureSave={handleSignatureSave}
            readOnly={false}
          />
        )}
      </div>
    </div>
  );
}
